// src/pages/admin/ResourceManager.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { db, storage } from '../../firebase';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  writeBatch
} from 'firebase/firestore';
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import {
  ArrowLeft,
  Plus,
  Edit2,
  Trash2,
  Upload,
  FileText,
  X,
  CheckCircle,
  AlertTriangle,
  Eye,
  EyeOff,
  GripVertical,
  Loader2,
  BookOpen,
  File,
  Image,
  Search,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import { PREFERRED_CATEGORIES } from '../../config/resourceCategories';

// ─── Constants ────────────────────────────────────────────────────────────────

const MAX_FILE_SIZE = 40 * 1024 * 1024; // 40 MB

const ACCEPTED_TYPES = {
  'application/pdf': { label: 'PDF', icon: FileText, color: 'text-red-500' },
  'image/jpeg': { label: 'JPG', icon: Image, color: 'text-blue-500' },
  'image/jpg': { label: 'JPG', icon: Image, color: 'text-blue-500' },
  'image/png': { label: 'PNG', icon: Image, color: 'text-green-500' },
  'application/msword': { label: 'DOC', icon: File, color: 'text-blue-700' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {
    label: 'DOCX',
    icon: File,
    color: 'text-blue-700'
  }
};

const ACCEPTED_EXTENSIONS = '.pdf,.jpg,.jpeg,.png,.doc,.docx';

const EMPTY_FORM = {
  title: '',
  description: '',
  categories: [],
  order: 0,
  isActive: true
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatFileSize(bytes) {
  if (!bytes || bytes === 0) return 'Unknown size';
  const mb = bytes / 1024 / 1024;
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  const kb = bytes / 1024;
  return `${kb.toFixed(0)} KB`;
}

function sanitizeFilename(name) {
  return name
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/_+/g, '_')
    .toLowerCase();
}

function getFileTypeInfo(mimeType) {
  return ACCEPTED_TYPES[mimeType] || { label: 'FILE', icon: FileText, color: 'text-gray-500' };
}

// ─── Main Component ───────────────────────────────────────────────────────────

function ResourceManager() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Data
  const [resources, setResources] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Search & sort
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('order');
  const [sortDir, setSortDir] = useState('asc');

  // Drag-and-drop reorder state
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [isSavingOrder, setIsSavingOrder] = useState(false);

  // Flash messages
  const [successMessage, setSuccessMessage] = useState(null);
  const [error, setError] = useState(null);

  // Modal state: null | { mode: 'add' } | { mode: 'edit', resource }
  const [formModal, setFormModal] = useState(null);

  // Delete confirm state
  const [deleteConfirm, setDeleteConfirm] = useState(null); // resource to delete

  // ── Load resources ──────────────────────────────────────────────────────────

  const fetchResources = useCallback(async () => {
    setIsLoading(true);
    try {
      const q = query(collection(db, 'resourceFiles'), orderBy('order', 'asc'));
      const snap = await getDocs(q);
      const data = [];
      snap.forEach(d => data.push({ id: d.id, ...d.data() }));
      setResources(data);
    } catch (err) {
      console.error('Error loading resources:', err);
      showError('Failed to load resources.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchResources();
  }, [fetchResources]);

  // ── Flash helpers ───────────────────────────────────────────────────────────

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 4000);
  };

  const showError = (msg) => {
    setError(msg);
    setTimeout(() => setError(null), 6000);
  };

  // ── Toggle active ───────────────────────────────────────────────────────────

  const handleToggleActive = async (resource) => {
    try {
      await updateDoc(doc(db, 'resourceFiles', resource.id), {
        isActive: !resource.isActive,
        updatedAt: new Date().toISOString()
      });
      showSuccess(`"${resource.title}" ${!resource.isActive ? 'published' : 'unpublished'}.`);
      fetchResources();
    } catch (err) {
      console.error('Toggle active error:', err);
      showError('Failed to update resource visibility.');
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────────────

  const handleDeleteConfirmed = async () => {
    const resource = deleteConfirm;
    setDeleteConfirm(null);
    try {
      // Delete from Firebase Storage
      if (resource.storagePath) {
        try {
          const storageRef = ref(storage, resource.storagePath);
          await deleteObject(storageRef);
        } catch (storageErr) {
          // File might already be gone — don't block the Firestore delete
          console.warn('Storage delete warning:', storageErr.message);
        }
      }
      // Delete from Firestore
      await deleteDoc(doc(db, 'resourceFiles', resource.id));
      showSuccess(`"${resource.title}" deleted.`);
      fetchResources();
    } catch (err) {
      console.error('Delete error:', err);
      showError('Failed to delete resource.');
    }
  };

  // ── Drag-and-drop reorder ───────────────────────────────────────────────────

  const handleDragStart = (id) => {
    setDraggingId(id);
  };

  const handleDragOver = (id) => {
    if (id !== draggingId) setDragOverId(id);
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setDragOverId(null);
  };

  const handleDrop = async (targetId) => {
    setDraggingId(null);
    setDragOverId(null);

    if (!draggingId || draggingId === targetId) return;

    // Reorder the list in memory
    const oldList = [...resources];
    const fromIdx = oldList.findIndex(r => r.id === draggingId);
    const toIdx   = oldList.findIndex(r => r.id === targetId);
    if (fromIdx === -1 || toIdx === -1) return;

    const reordered = [...oldList];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);

    // Reindex sequentially — fixes any existing duplicates/gaps too
    const reindexed = reordered.map((r, i) => ({ ...r, order: i }));

    // Optimistic UI update
    setResources(reindexed);

    // Batch write all order changes to Firestore atomically
    setIsSavingOrder(true);
    try {
      const batch = writeBatch(db);
      reindexed.forEach(r => {
        batch.update(doc(db, 'resourceFiles', r.id), { order: r.order });
      });
      await batch.commit();
      showSuccess('Order saved.');
    } catch (err) {
      console.error('Reorder error:', err);
      showError('Failed to save new order. Reloading...');
      fetchResources(); // Roll back to server state
    } finally {
      setIsSavingOrder(false);
    }
  };

  // ── Search + sort ───────────────────────────────────────────────────────────

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const displayedResources = React.useMemo(() => {
    let list = [...resources];

    // Search across title, fileName, and categories
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      list = list.filter(r =>
        r.title?.toLowerCase().includes(term) ||
        r.fileName?.toLowerCase().includes(term) ||
        r.categories?.some(c => c.toLowerCase().includes(term))
      );
    }

    // Sort
    list.sort((a, b) => {
      let aVal, bVal;
      if (sortField === 'order') {
        aVal = a.order ?? 0;
        bVal = b.order ?? 0;
      } else if (sortField === 'title') {
        aVal = (a.title || '').toLowerCase();
        bVal = (b.title || '').toLowerCase();
      } else if (sortField === 'fileName') {
        aVal = (a.fileName || '').toLowerCase();
        bVal = (b.fileName || '').toLowerCase();
      } else if (sortField === 'fileSize') {
        aVal = a.fileSize || 0;
        bVal = b.fileSize || 0;
      } else if (sortField === 'uploadedAt') {
        aVal = a.uploadedAt || '';
        bVal = b.uploadedAt || '';
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
  }, [resources, searchTerm, sortField, sortDir]);

  // ── Render ──────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading resources...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto p-6 space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Resources</h1>
              <p className="text-sm text-gray-500">
                {resources.filter(r => r.isActive).length} published · {resources.filter(r => !r.isActive).length} hidden
                {isSavingOrder && <span className="ml-2 text-purple-500 italic">Saving order...</span>}
              </p>
            </div>
          </div>
          <button
            onClick={() => setFormModal({ mode: 'add' })}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Plus size={16} />
            Add Resource
          </button>
        </div>

        {/* Search + Sort toolbar */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 min-w-48">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search by title, filename, or category..."
              className="w-full pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Sort buttons */}
          <div className="flex items-center gap-1 text-xs text-gray-500 font-medium">
            <span className="mr-1">Sort:</span>
            {[
              { field: 'order', label: 'Order' },
              { field: 'title', label: 'Title' },
              { field: 'fileName', label: 'Filename' },
              { field: 'fileSize', label: 'Size' },
              { field: 'uploadedAt', label: 'Date' },
            ].map(({ field, label }) => (
              <button
                key={field}
                onClick={() => handleSort(field)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg border transition-colors ${
                  sortField === field
                    ? 'bg-purple-50 border-purple-300 text-purple-700'
                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {label}
                {sortField === field && (
                  sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Drag hint — only shown when sorted by Order */}
        {sortField === 'order' && !searchTerm.trim() && resources.length > 1 && (
          <p className="text-xs text-gray-400 flex items-center gap-1.5">
            <GripVertical size={13} />
            Drag rows to reorder. Order numbers will be reassigned automatically.
          </p>
        )}
        {sortField !== 'order' && (
          <p className="text-xs text-amber-500 flex items-center gap-1.5">
            <GripVertical size={13} />
            Switch sort to "Order" to enable drag-and-drop reordering.
          </p>
        )}

        {/* Match count when searching */}
        {searchTerm.trim() && (
          <p className="text-sm text-gray-500">
            {displayedResources.length} {displayedResources.length === 1 ? 'result' : 'results'} for "{searchTerm}"
          </p>
        )}

        {/* Flash messages */}
        {successMessage && (
          <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
            <CheckCircle size={16} />
            {successMessage}
          </div>
        )}
        {error && (
          <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            <AlertTriangle size={16} />
            {error}
            <button onClick={() => setError(null)} className="ml-auto">
              <X size={14} />
            </button>
          </div>
        )}

        {/* Resource list */}
        {resources.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <BookOpen size={40} className="text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No resources yet</p>
            <p className="text-sm text-gray-400 mt-1">Click "Add Resource" to upload your first file.</p>
          </div>
        ) : displayedResources.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Search size={40} className="text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No results found</p>
            <p className="text-sm text-gray-400 mt-1">Try a different search term.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {displayedResources.map(resource => (
              <ResourceRow
                key={resource.id}
                resource={resource}
                onEdit={() => setFormModal({ mode: 'edit', resource })}
                onDelete={() => setDeleteConfirm(resource)}
                onToggleActive={() => handleToggleActive(resource)}
                dragEnabled={sortField === 'order' && !searchTerm.trim()}
                isDragging={draggingId === resource.id}
                isDragOver={dragOverId === resource.id}
                onDragStart={() => handleDragStart(resource.id)}
                onDragOver={() => handleDragOver(resource.id)}
                onDragEnd={handleDragEnd}
                onDrop={() => handleDrop(resource.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      {formModal && (
        <ResourceFormModal
          mode={formModal.mode}
          resource={formModal.resource}
          existingCount={resources.length}
          onClose={() => setFormModal(null)}
          onSaved={(msg) => {
            setFormModal(null);
            showSuccess(msg);
            fetchResources();
          }}
          onError={showError}
        />
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <DeleteConfirmModal
          resource={deleteConfirm}
          onConfirm={handleDeleteConfirmed}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}
    </div>
  );
}

// ─── Resource Row ─────────────────────────────────────────────────────────────

function ResourceRow({
  resource, onEdit, onDelete, onToggleActive,
  dragEnabled, isDragging, isDragOver,
  onDragStart, onDragOver, onDragEnd, onDrop
}) {
  const typeInfo = getFileTypeInfo(resource.fileType);
  const TypeIcon = typeInfo.icon;

  return (
    <div
      draggable={dragEnabled}
      onDragStart={dragEnabled ? onDragStart : undefined}
      onDragOver={dragEnabled ? (e) => { e.preventDefault(); onDragOver(); } : undefined}
      onDragEnd={dragEnabled ? onDragEnd : undefined}
      onDrop={dragEnabled ? (e) => { e.preventDefault(); onDrop(); } : undefined}
      className={`bg-white rounded-xl border overflow-hidden transition-all ${
        isDragging   ? 'opacity-40 scale-95 border-purple-300' :
        isDragOver   ? 'border-purple-500 shadow-lg shadow-purple-100 ring-2 ring-purple-300' :
        resource.isActive ? 'border-gray-200' : 'border-gray-100 opacity-60'
      }`}
    >
      <div className="flex items-center gap-4 px-5 py-4 flex-wrap">

        {/* Drag handle */}
        <GripVertical
          size={16}
          className={`flex-shrink-0 transition-colors ${
            dragEnabled ? 'text-gray-400 cursor-grab active:cursor-grabbing' : 'text-gray-200 cursor-not-allowed'
          }`}
        />

        {/* File type icon */}
        <TypeIcon size={20} className={`flex-shrink-0 ${typeInfo.color}`} />

        {/* Title + meta */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-800 text-sm">{resource.title}</span>
            {!resource.isActive && (
              <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-medium">
                Hidden
              </span>
            )}
          </div>
          {resource.description && (
            <p className="text-xs text-gray-500 mt-0.5 truncate">{resource.description}</p>
          )}
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            {resource.fileName && (
              <span className="text-xs text-gray-400 font-mono truncate max-w-xs" title={resource.fileName}>
                {resource.fileName}
              </span>
            )}
            {resource.categories && resource.categories.map(cat => (
              <span key={cat} className="text-xs bg-purple-50 text-purple-600 px-2 py-0.5 rounded-full font-medium">
                {cat}
              </span>
            ))}
            <span className="text-xs text-gray-400">{formatFileSize(resource.fileSize)}</span>
            <span className="text-xs text-gray-400">Order: {resource.order}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={onToggleActive}
            title={resource.isActive ? 'Hide from users' : 'Publish to users'}
            className={`p-2 rounded-lg transition-colors text-sm ${
              resource.isActive
                ? 'text-green-600 hover:bg-green-50'
                : 'text-gray-400 hover:bg-gray-100'
            }`}
          >
            {resource.isActive ? <Eye size={16} /> : <EyeOff size={16} />}
          </button>
          <button
            onClick={onEdit}
            className="p-2 rounded-lg text-blue-500 hover:bg-blue-50 transition-colors"
            title="Edit metadata"
          >
            <Edit2 size={16} />
          </button>
          <button
            onClick={onDelete}
            className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
            title="Delete resource"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Resource Form Modal (Add & Edit) ─────────────────────────────────────────

function ResourceFormModal({ mode, resource, existingCount, onClose, onSaved, onError }) {
  const isEditing = mode === 'edit';

  // Form fields
  const [title, setTitle] = useState(resource?.title || '');
  const [description, setDescription] = useState(resource?.description || '');
  const [selectedCategories, setSelectedCategories] = useState(resource?.categories || []);
  const [customCategoryInput, setCustomCategoryInput] = useState('');
  const [order, setOrder] = useState(
    isEditing ? (resource?.order ?? 0) : existingCount
  );
  const [isActive, setIsActive] = useState(resource?.isActive ?? true);

  // File upload (add mode only)
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Upload progress
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  const fileInputRef = useRef(null);

  // ── Category helpers ────────────────────────────────────────────────────────

  const toggleCategory = (label) => {
    setSelectedCategories(prev =>
      prev.includes(label) ? prev.filter(c => c !== label) : [...prev, label]
    );
  };

  const addCustomCategory = () => {
    const trimmed = customCategoryInput.trim();
    if (!trimmed) return;
    if (selectedCategories.includes(trimmed)) {
      setCustomCategoryInput('');
      return;
    }
    setSelectedCategories(prev => [...prev, trimmed]);
    setCustomCategoryInput('');
  };

  const removeCategory = (label) => {
    setSelectedCategories(prev => prev.filter(c => c !== label));
  };

  // ── File selection ──────────────────────────────────────────────────────────

  const handleFileSelect = (file) => {
    setFileError(null);
    if (!ACCEPTED_TYPES[file.type]) {
      setFileError('Unsupported file type. Please use PDF, JPG, PNG, DOC, or DOCX.');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setFileError(`File is too large. Maximum size is 40 MB (this file is ${formatFileSize(file.size)}).`);
      return;
    }
    setSelectedFile(file);
    // Auto-fill title from filename if empty
    if (!title) {
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '').replace(/[_-]/g, ' ');
      setTitle(nameWithoutExt);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  // ── Save ────────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    // Validation
    if (!title.trim()) {
      onError('Please enter a title.');
      return;
    }
    if (!isEditing && !selectedFile) {
      onError('Please select a file to upload.');
      return;
    }

    setIsSaving(true);

    try {
      if (isEditing) {
        // ── Edit: update Firestore metadata only ──
        await updateDoc(doc(db, 'resourceFiles', resource.id), {
          title: title.trim(),
          description: description.trim(),
          categories: selectedCategories,
          order: Number(order),
          isActive,
          updatedAt: new Date().toISOString()
        });
        onSaved(`"${title.trim()}" updated successfully.`);

      } else {
        // ── Add: upload file then create Firestore doc ──
        const sanitized = sanitizeFilename(selectedFile.name);
        const timestamp = Date.now();
        const storagePath = `resources/${timestamp}_${sanitized}`;
        const storageRef = ref(storage, storagePath);

        // Upload with progress tracking
        await new Promise((resolve, reject) => {
          const uploadTask = uploadBytesResumable(storageRef, selectedFile, {
            contentType: selectedFile.type
          });

          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const pct = Math.round(
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100
              );
              setUploadProgress(pct);
            },
            (err) => reject(err),
            () => resolve()
          );
        });

        // Create Firestore document
        await addDoc(collection(db, 'resourceFiles'), {
          title: title.trim(),
          description: description.trim(),
          categories: selectedCategories,
          storagePath,
          fileName: selectedFile.name,
          fileSize: selectedFile.size,
          fileType: selectedFile.type,
          order: Number(order),
          isActive,
          uploadedAt: new Date().toISOString(),
          uploadedBy: 'admin',
          updatedAt: new Date().toISOString()
        });

        onSaved(`"${title.trim()}" uploaded successfully.`);
      }
    } catch (err) {
      console.error('Save error:', err);
      onError(`Failed to ${isEditing ? 'update' : 'upload'} resource. ${err.message}`);
      setIsSaving(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  // Custom categories = ones selected that aren't in PREFERRED_CATEGORIES
  const preferredLabels = PREFERRED_CATEGORIES.map(p => p.label);
  const customSelected = selectedCategories.filter(c => !preferredLabels.includes(c));

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">

        {/* Modal header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-800">
            {isEditing ? `Edit: ${resource.title}` : 'Add New Resource'}
          </h2>
          <button
            onClick={onClose}
            disabled={isSaving}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5">

          {/* File upload zone — add mode only */}
          {!isEditing && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                File <span className="text-red-500">*</span>
              </label>

              {selectedFile ? (
                <div className="flex items-center gap-3 p-4 bg-green-50 border-2 border-green-300 rounded-xl">
                  {(() => {
                    const info = getFileTypeInfo(selectedFile.type);
                    const Icon = info.icon;
                    return <Icon size={24} className={info.color} />;
                  })()}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{selectedFile.name}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(selectedFile.size)}</p>
                  </div>
                  <button
                    onClick={() => { setSelectedFile(null); setUploadProgress(0); }}
                    className="text-gray-400 hover:text-red-500 transition-colors"
                    disabled={isSaving}
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div
                  onDrop={handleDrop}
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                    isDragging
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-300 hover:border-purple-400 hover:bg-gray-50'
                  }`}
                >
                  <Upload size={32} className="mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600 font-medium">
                    Drag & drop or click to browse
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    PDF, JPG, PNG, DOC, DOCX · Max 40 MB
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_EXTENSIONS}
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) handleFileSelect(file);
                    }}
                    disabled={isSaving}
                  />
                </div>
              )}

              {fileError && (
                <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
                  <AlertTriangle size={12} /> {fileError}
                </p>
              )}
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Arabic Alphabet Worksheet – Beginners"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              disabled={isSaving}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the resource..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
              disabled={isSaving}
            />
          </div>

          {/* Categories */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Categories <span className="text-gray-400 font-normal">(optional)</span>
            </label>

            {/* Preferred category chips */}
            <div className="flex flex-wrap gap-2 mb-3">
              {PREFERRED_CATEGORIES.map(cat => {
                const isSelected = selectedCategories.includes(cat.label);
                return (
                  <button
                    key={cat.id}
                    onClick={() => toggleCategory(cat.label)}
                    disabled={isSaving}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                      isSelected
                        ? 'bg-purple-600 text-white border-purple-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-purple-400 hover:text-purple-600'
                    }`}
                  >
                    {cat.label}
                  </button>
                );
              })}
            </div>

            {/* Custom categories already selected */}
            {customSelected.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {customSelected.map(cat => (
                  <span
                    key={cat}
                    className="flex items-center gap-1 px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded-lg text-xs font-semibold"
                  >
                    {cat}
                    <button
                      onClick={() => removeCategory(cat)}
                      disabled={isSaving}
                      className="hover:text-red-600 transition-colors"
                    >
                      <X size={11} />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Add custom category */}
            <div className="flex gap-2">
              <input
                type="text"
                value={customCategoryInput}
                onChange={(e) => setCustomCategoryInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); addCustomCategory(); }
                }}
                placeholder="Add custom category..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                disabled={isSaving}
              />
              <button
                onClick={addCustomCategory}
                disabled={!customCategoryInput.trim() || isSaving}
                className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg text-sm font-medium transition-colors disabled:opacity-40"
              >
                Add
              </button>
            </div>
          </div>

          {/* Order + Active status row */}
          <div className="flex gap-4 items-end">
            <div className="w-28">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sort Order
              </label>
              <input
                type="number"
                value={order}
                min={0}
                onChange={(e) => setOrder(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                disabled={isSaving}
              />
              <p className="text-xs text-gray-400 mt-0.5">Lower = first</p>
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Visibility
              </label>
              <button
                onClick={() => setIsActive(v => !v)}
                disabled={isSaving}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-all w-full justify-center ${
                  isActive
                    ? 'bg-green-50 text-green-700 border-green-300 hover:bg-green-100'
                    : 'bg-gray-50 text-gray-500 border-gray-300 hover:bg-gray-100'
                }`}
              >
                {isActive ? <Eye size={16} /> : <EyeOff size={16} />}
                {isActive ? 'Published (visible to users)' : 'Hidden (not visible)'}
              </button>
            </div>
          </div>

          {/* Upload progress bar */}
          {isSaving && !isEditing && uploadProgress > 0 && (
            <div>
              <div className="flex justify-between text-xs text-gray-600 mb-1">
                <span>Uploading...</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Modal footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 text-gray-600 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || (!isEditing && !selectedFile)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                {isEditing ? 'Saving...' : uploadProgress < 100 ? `Uploading ${uploadProgress}%` : 'Saving...'}
              </>
            ) : (
              isEditing ? 'Save Changes' : 'Upload Resource'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Delete Confirm Modal ─────────────────────────────────────────────────────

function DeleteConfirmModal({ resource, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Trash2 size={18} className="text-red-600" />
          </div>
          <div>
            <h3 className="font-bold text-gray-800">Delete Resource</h3>
            <p className="text-sm text-gray-500">This cannot be undone.</p>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-3 mb-5 text-sm text-gray-700">
          <span className="font-medium">{resource.title}</span>
          <br />
          <span className="text-xs text-gray-400">{resource.fileName} · {formatFileSize(resource.fileSize)}</span>
        </div>

        <p className="text-sm text-gray-600 mb-5">
          This will permanently delete the file from Firebase Storage and remove it from the resources list for all users.
        </p>

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export default ResourceManager;